import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ArrowUpRight, MessageCircle, Sparkles } from 'lucide-react';
import { z } from 'zod';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useEmbedBridge } from '@/hooks/use-embed-bridge';
import { probeEndpointStatusWithTimeout } from '@/hooks/use-endpoint-status';
import { resolveAssetUrl } from '@/lib/asset-url';
import { bothChatModesAvailable } from '@/lib/chat-mode';
import { getTransportConfig } from '@/lib/runtime-env';
import { PICKER, testId } from '@/lib/selectors';
import { cn } from '@/lib/utils';

const homeSearchSchema = z.object({
	mode: z.enum(['reachout-ai', 'classic-chat']).optional(),
	features: z.string().optional(),
	// Carried-through params from the partner-page iframe URL — preserved on
	// the redirect so chat.tsx and useEmbedBridge can still see them.
	d: z.string().optional(),
	embed: z.enum(['floating', 'inline', 'ambient']).optional(),
});

export const Route = createFileRoute('/')({
	validateSearch: homeSearchSchema,
	// The picker is a *surface of its own*, gated like any other: it renders
	// only for districts entitled to both Chatbot and Helpline. Anything else —
	// a single-surface district, a hand-typed bare `/`, a stale embed — is
	// redirected into /chat, which resolves the one mode that district has.
	//
	// Server-authoritative for the same reason /chat is: flipping Features__c in
	// Salesforce must retract the picker from every embed already in the wild,
	// without school admins touching their CMS pages. The probe is cached and
	// raced against a short timeout, so a slow Lambda never stalls the student;
	// on timeout the baked `?features=` hint decides and /chat re-checks anyway.
	beforeLoad: async ({ search }) => {
		const districtId = search.d ?? getTransportConfig().districtId;
		const status = districtId
			? await probeEndpointStatusWithTimeout(districtId, 1500)
			: ({ state: 'unknown' } as const);

		if (!search.mode && bothChatModesAvailable({ urlFeatures: search.features, status })) {
			return;
		}

		throw redirect({
			to: '/chat',
			search: {
				mode: search.mode,
				features: search.features,
				d: search.d,
				embed: search.embed,
			},
		});
	},
	component: Home,
});

function Home() {
	const { embedded, mode } = useEmbedBridge();
	const search = Route.useSearch();
	const tight = embedded && mode === 'floating';
	const choiceSearch = (mode: 'reachout-ai' | 'classic-chat') => ({
		mode,
		features: search.features,
		d: search.d,
		embed: search.embed,
	});

	return (
		<main
			{...testId(PICKER.root)}
			className={cn(
				'reachout-picker flex-1 min-h-0 overflow-y-auto',
				tight && 'reachout-picker--tight',
			)}
		>
			<div className="mx-auto w-full max-w-lg">
				<img
					src={resolveAssetUrl('/reachout.png')}
					alt="ReachOut"
					width={88}
					height={64}
					className="mb-6 h-10 w-auto object-contain"
				/>
				<h1 className="mb-3 text-3xl font-semibold tracking-tight">What’s on your mind?</h1>
				<p className="mb-2 text-base leading-relaxed text-muted-foreground">
					School, friendships, stress, or something you’re figuring out.
				</p>
				<p className="mb-7 text-base leading-relaxed text-muted-foreground">
					Click one of the options below to start chatting.
				</p>
				<div className="flex flex-col gap-3">
					<Link
						to="/chat"
						search={choiceSearch('reachout-ai')}
						{...testId(PICKER.chooseAi)}
						className="reachout-choice reachout-choice--ai"
					>
						<Avatar className="size-12 shrink-0">
							<AvatarImage src={resolveAssetUrl('/nomi.png')} alt="" />
							<AvatarFallback>N</AvatarFallback>
						</Avatar>
						<div className="min-w-0 flex-1">
							<Badge variant="outline" className="mb-2">
								<Sparkles className="size-3" aria-hidden /> AI chat
							</Badge>
							<h2 className="text-lg font-semibold">Chat with Nomi</h2>
							<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
								An AI you can talk things through with, at your own pace.
							</p>
						</div>
						<ArrowUpRight className="size-5 shrink-0" aria-hidden />
					</Link>
					<Link
						to="/chat"
						search={choiceSearch('classic-chat')}
						{...testId(PICKER.chooseCounselor)}
						className="reachout-choice reachout-choice--human"
					>
						<Avatar className="size-12 shrink-0">
							<AvatarImage src={resolveAssetUrl('/counselor.png')} alt="" />
							<AvatarFallback>C</AvatarFallback>
						</Avatar>
						<div className="min-w-0 flex-1">
							<Badge variant="outline" className="mb-2">
								<MessageCircle className="size-3" aria-hidden /> Human support
							</Badge>
							<h2 className="text-lg font-semibold">Talk to a counselor</h2>
							<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
								A trained person who can listen and help you work through things.
							</p>
						</div>
						<ArrowUpRight className="size-5 shrink-0" aria-hidden />
					</Link>
				</div>
				<p className="mt-6 text-sm text-muted-foreground">Your conversations are confidential.</p>
			</div>
		</main>
	);
}
